import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, Share2, BookOpen, Tag, AlertCircle } from "lucide-react";
import SEOHead from "../components/SEOHead";
import OptimizedImage from "../components/OptimizedImage";
import { blogService, blogFAQService } from "../lib/database";
import type { BlogPost as BlogPostType, BlogFAQ } from "../lib/supabase";
import toast from "react-hot-toast";
import { useSEOData } from "../hooks/useSEOData";

const toAbsoluteUrl = (pathOrUrl?: string) => {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const origin = typeof window !== "undefined" ? window.location.origin : "https://gradvikalp.com";
  return `${origin}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  // SEO (loads first; prevents fallback flashing in <head/>)
  const { seoData, loading: seoLoading } = useSEOData("blogs", slug);

  const [post, setPost] = useState<BlogPostType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);
  const [faqs, setFaqs] = useState<BlogFAQ[]>([]);
  const [readingProgress, setReadingProgress] = useState(0);

  const fetchPost = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      setError(null);
      const data = await blogService.getBySlug(slug);

      if (!data) {
        setError("Blog post not found");
        return;
      }

      setPost(data);

      if (data.category) {
        const related = await blogService.getAll({ category: data.category, published: true });
        setRelatedPosts(related?.filter((p) => p?.id !== data?.id).slice(0, 2) || []);
      }

      try {
        const faqData = await blogFAQService.getByBlogId(data.id);
        setFaqs(faqData);
      } catch (faqErr) {
        console.error("Error fetching blog FAQs:", faqErr);
      }
    } catch (err) {
      console.error("Error fetching post:", err);
      setError("Failed to load blog post");
      toast.error("Failed to load blog post");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchPost(); }, [fetchPost]);

  useEffect(() => {
    const handleScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / (total || 1)) * 100;
      setReadingProgress(Math.min(100, Math.max(0, progress)));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sharePost = async () => {
    if (!post) return;
    const shareUrl = typeof window !== "undefined" ? window.location.href : toAbsoluteUrl(`/blogs/${post.slug}`);
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url: shareUrl });
      } catch (_) {
        await navigator.clipboard.writeText(shareUrl || "");
        toast.success("Link copied to clipboard!");
      }
    } else {
      await navigator.clipboard.writeText(shareUrl || "");
      toast.success("Link copied to clipboard!");
    }
  };

  if (!slug) {
    return (
      <div style={{ paddingTop: "80px" }} className="bg-light">
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: "600px" }}>
              <div className="card-body p-5">
                <AlertCircle className="text-danger mb-3" size={64} />
                <h1 className="h2 text-dark mb-3">Invalid Blog URL</h1>
                <p className="text-muted mb-4">The blog post URL is missing or invalid.</p>
                <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ paddingTop: "80px" }} className="bg-light">
        {/* Render SEO only when ready (prevents default overrides) */}
        {!seoLoading && seoData && <SEOHead {...seoData} />}
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading blog post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ paddingTop: "80px" }} className="bg-light">
        {!seoLoading && seoData && <SEOHead {...seoData} />}
        <div className="container py-5">
          <div className="text-center">
            <div className="card border-0 shadow-lg mx-auto" style={{ maxWidth: "600px" }}>
              <div className="card-body p-5">
                <AlertCircle className="text-danger mb-3" size={64} />
                <h1 className="h2 text-dark mb-3">{error || "Blog post not found"}</h1>
                <p className="text-muted mb-4">
                  The blog post you're looking for doesn't exist or may have been removed.
                </p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
                  <button onClick={() => fetchPost()} className="btn btn-outline-primary">Retry</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Final render
  const absHero = toAbsoluteUrl(post.image_url || "/Grad-Vikalp.png");
  const pageUrl = toAbsoluteUrl(`/blogs/${post.slug}`) as string;

  // JSON-LD aligned with head tags
  const articleLD = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt || post.meta_description || "",
    image: absHero,
    author: { "@type": "Person", name: post.author || "Grad Vikalp Team" },
    publisher: {
      "@type": "Organization",
      name: "Grad Vikalp",
      logo: { "@type": "ImageObject", url: toAbsoluteUrl("/Grad-Vikalp.png") },
    },
    datePublished: post.created_at || undefined,
    dateModified: post.updated_at || post.created_at || undefined,
    mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
    articleSection: post.category || "Education",
    keywords: (post.tags && post.tags.length ? post.tags : undefined) || undefined,
    wordCount: post.content ? post.content.replace(/<[^>]*>/g, "").split(/\s+/).length : undefined,
    articleBody: post.content
      ? post.content.replace(/<[^>]*>/g, "").slice(0, 1000)
      : undefined,
  };

  return (
    <div style={{ paddingTop: "80px" }} className="bg-light">
      {/* Head SEO from DB */}
      {seoData && <SEOHead {...seoData} />}

      {/* Reading Progress */}
      <div
        className="position-fixed top-0 start-0 bg-primary"
        style={{ height: "3px", width: `${readingProgress}%`, zIndex: 1051, transition: "width 0.3s ease" }}
      />

      <div className="container py-5">
        <Link to="/blog" className="btn btn-link text-primary text-decoration-none mb-4 p-0">
          <ArrowLeft className="me-2" size={16} />
          Back to Blog
        </Link>

        <article className="card border-0 shadow-lg">
          <div className="position-relative">
            <OptimizedImage
              src={absHero!}
              alt={`${post.title} - ${post.category || "Education"} article by ${post.author || "Grad Vikalp"}`}
              title={`${post.title} | ${post.category || "Guide"} | By ${post.author || "Grad Vikalp"} | ${post.read_time || "5 min read"} | Published: ${post.created_at ? new Date(post.created_at).toLocaleDateString() : "Recently"}`}
              className="card-img-top"
              style={{ height: "400px", objectFit: "cover" }}
              priority={true}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
            />
            <div className="position-absolute top-0 start-0 end-0 bottom-0 bg-dark bg-opacity-50"></div>
            <div className="position-absolute bottom-0 start-0 end-0 p-4 text-white">
              <div className="mb-3">
                <span className="badge bg-primary fs-6 px-3 py-2">{post.category || "Guide"}</span>
                {post.featured && <span className="badge bg-warning fs-6 px-3 py-2 ms-2">Featured Article</span>}
              </div>
              <h1 className="display-5 fw-bold mb-2">{post.title}</h1>
              <p className="lead opacity-90 mb-0">{post.excerpt || "Read this comprehensive guide for valuable insights."}</p>
            </div>
          </div>

          <div className="card-body p-5">
            {/* Article Meta */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-5 pb-4 border-bottom">
              <div className="d-flex align-items-center gap-3 mb-3 mb-md-0">
                {post.author_image && (
                  <OptimizedImage
                    src={toAbsoluteUrl(post.author_image)!}
                    alt={`${post.author || "Author"} - Author profile picture`}
                    title={`${post.author || "Author"} - Education Expert at Grad Vikalp`}
                    className="rounded-circle"
                    style={{ width: "50px", height: "50px", objectFit: "cover" }}
                    loading="lazy"
                  />
                )}
                <div>
                  <div className="fw-semibold text-dark">{post.author || "Grad Vikalp Team"}</div>
                  <div className="d-flex align-items-center gap-3 text-muted small">
                    <div className="d-flex align-items-center gap-1">
                      <Calendar size={14} />
                      <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : "Recently"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <Clock size={14} />
                      <span>{post.read_time || "5 min read"}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button onClick={sharePost} className="btn btn-primary d-flex align-items-center gap-2" type="button">
                  <Share2 size={16} />
                  <span>Share</span>
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="alert alert-info mb-4">
              <h2 className="h5 fw-bold mb-2">📖 Article Summary</h2>
              <p className="mb-0">{post.excerpt || "This article provides comprehensive guidance and insights."}</p>
            </div>

            {/* Content */}
            <div
              id="article-content"
              className="mb-5 blog-content"
              style={{ fontSize: "1.1rem", lineHeight: "1.7" }}
              dangerouslySetInnerHTML={{ __html: post.content || "<p>Content is loading...</p>" }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <Tag size={16} className="text-muted" />
                  <span className="fw-semibold text-dark">Tags:</span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {post.tags.map((tag: string, i: number) => (
                    <Link key={i} to={`/blog?search=${encodeURIComponent(tag)}`} className="badge bg-primary bg-opacity-10 text-primary fs-6 px-3 py-2 text-decoration-none">
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ */}
            {faqs.length > 0 && (
              <div className="card border-0 bg-light mb-5">
                <div className="card-body p-4">
                  <h3 className="h4 fw-bold text-dark mb-4">❓ Frequently Asked Questions</h3>
                  <div className="accordion" id="faqAccordion">
                    {faqs.map((faq, index) => (
                      <div key={faq.id} className="accordion-item border-0 bg-transparent">
                        <h4 className="accordion-header">
                          <button
                            className={`accordion-button ${index !== 0 ? "collapsed" : ""} bg-transparent border-0 fw-semibold`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#faq${index + 1}`}
                          >
                            {faq.question}
                          </button>
                        </h4>
                        <div id={`faq${index + 1}`} className={`accordion-collapse collapse ${index === 0 ? "show" : ""}`} data-bs-parent="#faqAccordion">
                          <div className="accordion-body">{faq.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Author Bio */}
            <div className="bg-light rounded-3 p-4 mb-5">
              <div className="d-flex align-items-start gap-3">
                {post.author_image && (
                  <OptimizedImage
                    src={toAbsoluteUrl(post.author_image)!}
                    alt={`${post.author || "Author"} - Author profile picture`}
                    title={`About ${post.author || "Author"}`}
                    className="rounded-circle"
                    style={{ width: "60px", height: "60px", objectFit: "cover" }}
                    loading="lazy"
                  />
                )}
                <div>
                  <h3 className="h5 fw-bold text-dark mb-2">About {post.author || "Our Team"}</h3>
                  <p className="text-muted mb-0">
                    {post.author || "Our team"} specializes in {post.category?.toLowerCase() || "education"} guidance,
                    college admissions, and career counseling.
                  </p>
                  <div className="mt-3">
                    <Link to={`/blog?search=${encodeURIComponent(post.author || "")}`} className="btn btn-sm btn-outline-primary">
                      View all articles by {post.author || "this author"}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Related */}
            {relatedPosts?.length > 0 && (
              <div>
                <h3 className="h2 fw-bold text-dark mb-4">📚 Related {post.category || "Education"} Articles</h3>
                <div className="row g-3">
                  {relatedPosts.map((rp) => (
                    <div key={rp.id} className="col-md-6">
                      <Link to={`/blog/${rp.slug}`} className="card border-0 bg-light text-decoration-none h-100">
                        <div className="card-body p-3">
                          <div className="d-flex gap-3">
                            <OptimizedImage
                              src={toAbsoluteUrl(rp.image_url || "https://images.pexels.com/photos/5212324/pexels-photo-5212324.jpeg?auto=compress&cs=tinysrgb&w=400")!}
                              alt={`${rp.title || "Related article"} - Related article`}
                              title={`${rp.title || "Related article"} | ${rp.category || "Guide"} | ${rp.read_time || "5 min read"}`}
                              className="rounded"
                              style={{ width: "80px", height: "80px", objectFit: "cover" }}
                              loading="lazy"
                            />
                            <div className="flex-grow-1">
                              <h4 className="h6 fw-semibold text-dark mb-2 line-clamp-2">{rp.title || "Related Article"}</h4>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-primary small fw-semibold">Read More →</span>
                                <span className="badge bg-primary">{rp.category || "Guide"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="card bg-primary text-white border-0 mb-5">
              <div className="card-body p-4 text-center">
                <h3 className="h4 fw-bold mb-3">🎯 Ready to Find Your Dream College?</h3>
                <p className="mb-4">Get personalized college recommendations based on your profile.</p>
                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                  <Link to="/neet-college-predictor" className="btn btn-light text-primary fw-semibold">Get Personalized List</Link>
                  <Link to="/college-reviews" className="btn btn-outline-light">Browse College Reviews</Link>
                </div>
              </div>
            </div>

            {/* JSON-LD (Article) */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLD) }} />
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost;